/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ObservableResult } from '../../../shared/store';
import {
  AnalyticsCourse,
  CourseActivityOverview,
  CourseAggregateMetrics,
  CourseGrade,
  StudentCourseMetrics,
} from '../models/analytics.model';
import { AnalyticsDataService } from './analytics-data.service';

export class MockAnalyticsDataService implements AnalyticsDataService {
  private mockCourses: AnalyticsCourse[] = [
    {
      organizationUid: '607c7fea08d83a0f46ef76ac',
      canvasCourseId: 9,
      courseName: 'Digital Proficiency',
      courseCode: 'DP101',
      startAt: '2024-09-01T00:00:00Z',
      endAt: '2024-12-15T23:59:59Z',
      workflowState: 'AVAILABLE',
      lastSyncAt: '2024-12-20T10:30:00Z',
    },
    {
      organizationUid: '607c7fea08d83a0f46ef76ac',
      canvasCourseId: 15,
      courseName: 'Web Development Fundamentals',
      courseCode: 'WD201',
      startAt: '2024-09-01T00:00:00Z',
      endAt: '2024-12-15T23:59:59Z',
      workflowState: 'AVAILABLE',
      lastSyncAt: '2024-12-20T10:30:00Z',
    },
    {
      organizationUid: '607c7fea08d83a0f46ef76ac',
      canvasCourseId: 22,
      courseName: 'Software Engineering Principles',
      courseCode: 'SE301',
      startAt: '2024-09-01T00:00:00Z',
      endAt: '2024-12-15T23:59:59Z',
      workflowState: 'AVAILABLE',
      lastSyncAt: '2024-12-20T10:30:00Z',
    },
    {
      organizationUid: '607c7fea08d83a0f46ef76ac',
      canvasCourseId: 18,
      courseName: 'Professional Development',
      courseCode: 'PD150',
      startAt: '2024-06-01T00:00:00Z',
      endAt: '2024-08-31T23:59:59Z',
      workflowState: 'COMPLETED',
      lastSyncAt: '2024-12-20T10:30:00Z',
    },
  ];

  getCourses(): ObservableResult<AnalyticsCourse[]> {
    return ObservableResult.ofSuccess(this.mockCourses);
  }

  getCourseGrades(courseId: number): ObservableResult<CourseGrade[]> {
    const grades: CourseGrade[] = [
      {
        canvasUserId: 1001,
        potentiallyUserUid: 'user-001',
        currentScore: 81.5,
        finalScore: 81.5,
        currentGrade: 'B-',
        finalGrade: 'B-',
        enrollmentState: 'active',
      },
      {
        canvasUserId: 1002,
        potentiallyUserUid: 'user-002',
        currentScore: 92,
        finalScore: 92,
        currentGrade: 'A',
        finalGrade: 'A',
        enrollmentState: 'active',
      },
      {
        canvasUserId: 1003,
        potentiallyUserUid: 'user-003',
        currentScore: 55,
        finalScore: 55,
        currentGrade: 'F',
        finalGrade: 'F',
        enrollmentState: 'active',
      },
      {
        canvasUserId: 1004,
        potentiallyUserUid: 'user-004',
        currentScore: 73,
        finalScore: 73,
        currentGrade: 'C',
        finalGrade: 'C',
        enrollmentState: 'active',
      },
      {
        canvasUserId: 1005,
        potentiallyUserUid: 'user-005',
        currentScore: 88,
        finalScore: 88,
        currentGrade: 'B+',
        finalGrade: 'B+',
        enrollmentState: 'completed',
      },
    ];
    return ObservableResult.ofSuccess(grades);
  }

  getCourseMetrics(courseId: number): ObservableResult<CourseAggregateMetrics> {
    const metrics: CourseAggregateMetrics = {
      canvasCourseId: courseId,
      courseName: this.mockCourses.find((c) => c.canvasCourseId === courseId)?.courseName || 'Unknown Course',
      organizationUid: '607c7fea08d83a0f46ef76ac',
      totalEnrollments: 48,
      activeStudents: 45,
      completedStudents: 38,
      passCount: 35,
      failCount: 10,
      passRate: 77.8,
      gradeDistribution: {
        mean: 76.5,
        standardDeviation: 12.3,
        buckets: [
          { rangeStart: 90, rangeEnd: 100, label: 'A', count: 8, percentage: 17.8 },
          { rangeStart: 80, rangeEnd: 89, label: 'B', count: 12, percentage: 26.7 },
          { rangeStart: 70, rangeEnd: 79, label: 'C', count: 10, percentage: 22.2 },
          { rangeStart: 60, rangeEnd: 69, label: 'D', count: 5, percentage: 11.1 },
          { rangeStart: 0, rangeEnd: 59, label: 'F', count: 10, percentage: 22.2 },
        ],
      },
      preAssessmentMetrics: {
        assignmentId: 101,
        assignmentName: 'Pre-Assessment Quiz',
        category: 'PRE_ASSESSMENT',
        totalStudents: 45,
        submittedCount: 44,
        gradedCount: 44,
        missingCount: 1,
        lateCount: 2,
        averageScore: 62.5,
        averagePercentage: 62.5,
        highestScore: 95,
        lowestScore: 28,
        medianScore: 65,
        standardDeviation: 15.2,
        passCount: 28,
        failCount: 16,
        passRate: 63.6,
      },
      postAssessmentMetrics: {
        assignmentId: 102,
        assignmentName: 'Post-Assessment Quiz',
        category: 'POST_ASSESSMENT',
        totalStudents: 45,
        submittedCount: 43,
        gradedCount: 43,
        missingCount: 2,
        lateCount: 3,
        averageScore: 78.3,
        averagePercentage: 78.3,
        highestScore: 100,
        lowestScore: 45,
        medianScore: 80,
        standardDeviation: 13.8,
        passCount: 36,
        failCount: 7,
        passRate: 83.7,
      },
      finalProjectMetrics: {
        assignmentId: 103,
        assignmentName: 'Final Project',
        category: 'FINAL_PROJECT',
        totalStudents: 45,
        submittedCount: 40,
        gradedCount: 38,
        missingCount: 5,
        lateCount: 4,
        averageScore: 81.2,
        averagePercentage: 81.2,
        highestScore: 98,
        lowestScore: 52,
        medianScore: 83,
        standardDeviation: 11.5,
        passCount: 34,
        failCount: 4,
        passRate: 89.5,
      },
      learningGapAnalysis: {
        preAssessmentAverage: 62.5,
        postAssessmentAverage: 78.3,
        overallGrowth: 25.3,
        highPerformersPreAvg: 85.2,
        highPerformersPostAvg: 92.1,
        highPerformersGrowth: 8.1,
        lowPerformersPreAvg: 42.3,
        lowPerformersPostAvg: 65.8,
        lowPerformersGrowth: 55.6,
        gapReduction: 38.5,
      },
      completionRate: 84.4,
    };
    return ObservableResult.ofSuccess(metrics);
  }

  getCourseActivityOverview(courseId: number): ObservableResult<CourseActivityOverview> {
    const activityOverview: CourseActivityOverview = {
      courseId: courseId,
      totalStudents: 45,
      activityLevelDistribution: {
        1: 8,
        2: 12,
        3: 15,
        4: 7,
        5: 3,
      },
      highActivityStudents: 10,
      lowActivityStudents: 20,
      totalTrainingHours: 125.8,
      averageTrainingHoursPerStudent: 2.8,
    };
    return ObservableResult.ofSuccess(activityOverview);
  }

  getMyMetrics(courseId: number): ObservableResult<StudentCourseMetrics> {
    const metrics: StudentCourseMetrics = {
      canvasCourseId: courseId,
      courseName: this.mockCourses.find((c) => c.canvasCourseId === courseId)?.courseName || 'Digital Proficiency',
      canvasUserId: 13,
      potentiallyUserUid: '9c2d8edc-aac5-4577-9a81-134a3983c757',
      finalScore: null,
      finalGrade: null,
      currentScore: null,
      currentGrade: null,
      passStatus: 'NOT_GRADED',
      preAssessment: {
        assignmentId: 91,
        assignmentName: 'Pre-Assessment Check in!',
        category: 'PRE_ASSESSMENT',
        score: 60.0,
        pointsPossible: 100.0,
        percentage: 60.0,
        passStatus: 'PASS',
        submissionStatus: 'GRADED',
        submittedAt: '2025-12-19T16:49:54Z',
        gradedAt: '2026-01-08T13:44:51Z',
        dueAt: null,
        late: false,
        missing: false,
      },
      postAssessment: {
        assignmentId: 90,
        assignmentName: 'Post-Assessment Check out!',
        category: 'POST_ASSESSMENT',
        score: 60.0,
        pointsPossible: 100.0,
        percentage: 60.0,
        passStatus: 'PASS',
        submissionStatus: 'GRADED',
        submittedAt: '2025-12-22T14:15:13Z',
        gradedAt: '2025-12-22T14:17:41Z',
        dueAt: null,
        late: false,
        missing: false,
      },
      finalProject: {
        assignmentId: 92,
        assignmentName: 'A1.3 Upload final project here',
        category: 'FINAL_PROJECT',
        score: 80.0,
        pointsPossible: 100.0,
        percentage: 80.0,
        passStatus: 'PASS',
        submissionStatus: 'GRADED',
        submittedAt: '2025-12-22T14:14:58Z',
        gradedAt: '2025-12-22T14:17:38Z',
        dueAt: null,
        late: false,
        missing: false,
      },
      otherAssignments: [
        {
          assignmentId: 35,
          assignmentName: 'Unnamed Quiz',
          category: 'OTHER',
          score: 2.0,
          pointsPossible: 3.0,
          percentage: 66.67,
          passStatus: 'PASS',
          submissionStatus: 'GRADED',
          submittedAt: '2025-10-01T12:01:48Z',
          gradedAt: '2025-10-01T12:01:48Z',
          dueAt: null,
          late: false,
          missing: false,
        },
        {
          assignmentId: 37,
          assignmentName: 'Unnamed Quiz 2',
          category: 'OTHER',
          score: 2.0,
          pointsPossible: 3.0,
          percentage: 66.67,
          passStatus: 'PASS',
          submissionStatus: 'GRADED',
          submittedAt: '2025-10-02T14:16:32Z',
          gradedAt: '2025-10-02T14:16:32Z',
          dueAt: null,
          late: false,
          missing: false,
        },
      ],
      learningGrowth: {
        preAssessmentScore: 60.0,
        postAssessmentScore: 60.0,
        growthPercentage: 0.0,
        growthPoints: 0.0,
      },
      totalActivityTimeSeconds: 2509,
      enrollmentState: 'active',
    };
    return ObservableResult.ofSuccess(metrics);
  }
}
